// Meeting capture API client (openspec/meeting-capture.md §4). Self-contained so
// it does not bloat lib/api.ts. Behind FEATURE_MEETING (routes 404 when dark).
import { authHeaders } from "./auth";
import type { MeetingSummaryBlock } from "@shared/types";

export interface MeetingListItem {
  id: string;
  title: string | null;
  status: string;
  durationSec: number | null;
  createdAt: string;
  conversationId: string | null;
  /** PH-4 My notes (present on detail; may be undefined on list payloads). */
  notes?: string | null;
}
export interface MeetingSegmentItem { idx: number; startMs: number; endMs: number; speaker: string; text: string; }
export type MeetingSummaryData = Omit<MeetingSummaryBlock, "id" | "type" | "meetingId" | "title" | "nextSteps"> & Partial<Pick<MeetingSummaryBlock, "nextSteps">>;
export interface MeetingDetail {
  meeting: MeetingListItem & { conversationId: string | null };
  segments: MeetingSegmentItem[];
  summary: MeetingSummaryData | null;
}

const j = (extra?: Record<string, string>): Record<string, string> => authHeaders({ Accept: "application/json", ...(extra ?? {}) });

export async function createMeeting(title?: string): Promise<{ id: string; conversationId: string }> {
  const res = await fetch("/api/meeting", { method: "POST", headers: j({ "Content-Type": "application/json" }), body: JSON.stringify({ title }) });
  if (!res.ok) throw new Error(`createMeeting ${res.status}`);
  return res.json() as Promise<{ id: string; conversationId: string }>;
}

export async function uploadSegment(meetingId: string, seg: { idx: number; startMs: number; endMs: number; audioBase64: string; format: string }): Promise<{ ok: boolean; text: string }> {
  const res = await fetch(`/api/meeting/${meetingId}/segment`, { method: "POST", headers: j({ "Content-Type": "application/json" }), body: JSON.stringify(seg) });
  if (!res.ok) throw new Error(`uploadSegment ${res.status}`);
  return res.json() as Promise<{ ok: boolean; text: string }>;
}

export async function stopMeeting(meetingId: string): Promise<{ status: string; conversationId: string | null; summary: MeetingSummaryData }> {
  const res = await fetch(`/api/meeting/${meetingId}/stop`, { method: "POST", headers: j() });
  if (!res.ok) throw new Error(`stopMeeting ${res.status}`);
  return res.json() as Promise<{ status: string; conversationId: string | null; summary: MeetingSummaryData }>;
}

/** A4 (MEETING_IMPORT): one audio file → transcribe → summary → meeting. */
export async function importMeeting(args: { title?: string; audioBase64: string; format: string }): Promise<{ id: string; status: string; conversationId: string | null; summary: MeetingSummaryData }> {
  const res = await fetch("/api/meeting/import", { method: "POST", headers: j({ "Content-Type": "application/json" }), body: JSON.stringify(args) });
  if (!res.ok) throw new Error(`importMeeting ${res.status}`);
  return res.json() as Promise<{ id: string; status: string; conversationId: string | null; summary: MeetingSummaryData }>;
}

/** §20 (MEETING_IMPORT): re-upload the meeting's audio → re-run transcribe +
 * summary in place (segments replaced, summary gets a new version). */
export async function retranscribeMeeting(meetingId: string, args: { audioBase64: string; format: string }): Promise<{ id: string; status: string; conversationId: string | null; summary: MeetingSummaryData }> {
  const res = await fetch(`/api/meeting/${meetingId}/retranscribe`, { method: "POST", headers: j({ "Content-Type": "application/json" }), body: JSON.stringify(args) });
  if (!res.ok) throw new Error(`retranscribeMeeting ${res.status}`);
  return res.json() as Promise<{ id: string; status: string; conversationId: string | null; summary: MeetingSummaryData }>;
}

export async function getMeeting(meetingId: string): Promise<MeetingDetail> {
  const res = await fetch(`/api/meeting/${meetingId}`, { headers: j() });
  if (!res.ok) throw new Error(`getMeeting ${res.status}`);
  return res.json() as Promise<MeetingDetail>;
}

export async function listMeetings(): Promise<MeetingListItem[]> {
  const res = await fetch("/api/meeting", { headers: j() });
  if (!res.ok) throw new Error(`listMeetings ${res.status}`);
  const body = (await res.json()) as { meetings: MeetingListItem[] };
  return body.meetings;
}

export interface DispatchAssignment { role: string; roleLabel: string; item: { text: string }; source: string; profile?: { id: string; label: string }; }
export interface DispatchPhase { label: string; mode: "parallel" | "serial"; assignments: DispatchAssignment[]; }
export interface DispatchPlan { phases: DispatchPhase[]; itemCount: number; etaMinutes: number | null; costEstimate: string | null; }
export interface DispatchOutcome { dispatched: boolean; reason: string; ids: string[]; feishu?: { sent: boolean; reason?: string }; }

export async function getDispatchPlan(meetingId: string): Promise<DispatchPlan> {
  const res = await fetch(`/api/meeting/${meetingId}/plan`, { method: "POST", headers: j() });
  if (!res.ok) throw new Error(`plan ${res.status}`);
  return (await res.json() as { plan: DispatchPlan }).plan;
}

export async function dispatchMeeting(meetingId: string): Promise<DispatchOutcome> {
  const res = await fetch(`/api/meeting/${meetingId}/dispatch`, { method: "POST", headers: j() });
  if (!res.ok) throw new Error(`dispatch ${res.status}`);
  return (await res.json() as { outcome: DispatchOutcome }).outcome;
}

// PH-4 My notes (MEETING_NOTES). Both 404 when the flag is dark.
export async function updateMeetingNotes(meetingId: string, notes: string): Promise<void> {
  const res = await fetch(`/api/meeting/${meetingId}/notes`, { method: "PUT", headers: j({ "Content-Type": "application/json" }), body: JSON.stringify({ notes }) });
  if (!res.ok) throw new Error(`updateNotes ${res.status}`);
}

/** AI Enhance: returns polished notes for the user to review — does NOT save. */
export async function enhanceMeetingNotes(meetingId: string, notes: string): Promise<string> {
  const res = await fetch(`/api/meeting/${meetingId}/notes/enhance`, { method: "POST", headers: j({ "Content-Type": "application/json" }), body: JSON.stringify({ notes }) });
  if (!res.ok) throw new Error(`enhanceNotes ${res.status}`);
  return (await res.json() as { enhanced: string }).enhanced;
}

/** PH-6b live preview: transcribe an in-progress take WITHOUT saving a segment
 * (reuses the voice STT endpoint). Returns "" on any failure (best-effort). */
export async function transcribePreview(audioBase64: string, format: string): Promise<string> {
  try {
    const res = await fetch("/api/voice/transcribe", { method: "POST", headers: j({ "Content-Type": "application/json" }), body: JSON.stringify({ audio_data: audioBase64, audio_format: format }) });
    if (!res.ok) return "";
    return ((await res.json()) as { text?: string }).text ?? "";
  } catch { return ""; }
}

// B1 daemon 桌面采集 (openspec/meeting-daemon.md §4/§8; MEETING_SYSTEM_AUDIO,
// routes 404 when dark).
export interface DaemonDeviceView { id: string; name: string; online: boolean; lastSeenAt: string | null; createdAt: string; }

export async function daemonPairInit(): Promise<{ code: string; expiresInSec: number }> {
  const res = await fetch("/api/daemon/pair/init", { method: "POST", headers: j() });
  if (!res.ok) throw new Error(`daemonPairInit ${res.status}`);
  return res.json() as Promise<{ code: string; expiresInSec: number }>;
}

export async function daemonStatus(): Promise<{ devices: DaemonDeviceView[] }> {
  const res = await fetch("/api/daemon/status", { headers: j() });
  if (!res.ok) throw new Error(`daemonStatus ${res.status}`);
  return res.json() as Promise<{ devices: DaemonDeviceView[] }>;
}

export async function daemonRevoke(deviceId: string): Promise<void> {
  const res = await fetch("/api/daemon/revoke", { method: "POST", headers: j({ "Content-Type": "application/json" }), body: JSON.stringify({ deviceId }) });
  if (!res.ok) throw new Error(`daemonRevoke ${res.status}`);
}

/** Blob → bare base64 (no data: prefix), chunked to avoid call-stack blowups. */
export async function blobToBase64(blob: Blob): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  let bin = "";
  const CH = 0x8000;
  for (let i = 0; i < buf.length; i += CH) bin += String.fromCharCode(...buf.subarray(i, i + CH));
  return btoa(bin);
}
